import { useEffect, useState } from "react";
import { googleLogout, useGoogleLogin } from "@react-oauth/google";
import { signOutFromFirebase } from "../api/authApi";
import { isSafari } from "react-device-detect";
import { parseJwt } from "../../common/utils/generalUtils";
import { deleteCookie, getCookie } from "../../common/utils/cookiesHandler";
import routes from "../../common/models/route-constants";

const clientId = process.env.REACT_APP_GOOGLE_LOGIN_CLIENT_ID;

export function useGoogleAuthState() {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [cookieToken, setCookieToken] = useState(null);

  let invalidEmailError = false;

  useEffect(() => {
    const token = getCookie("token");
    setCookieToken(token);
  }, []);

  useEffect(() => {
    if (cookieToken) {
      setLoading(true);
      onLoginSuccess(cookieToken);
    } else {
      setLoading(false);
    }
  }, [cookieToken]);

  /**
   *
   * Called when user is successfully logged in to google
   * @param {Object} res - The response object received from google login api.
   * More details on the response object here : https://developers.google.com/identity/sign-in/web/reference#users
   */
  const onLoginSuccess = (res) => {
    const response = parseJwt(res.credential);
    const responseObj = {
      ...response,
      profileObj: { ...response },
      id_token: res.credential,
    };

    setUser(responseObj);
    setError(null);
    setLoading(false);
  };

  /**
   *
   *  Called when user is successfully logged out of google
   */
  const onLogoutSuccess = () => {
    // After logout from google is done, logout from firebase
    signOutFromFirebase(() => {
      deleteCookie("user");
      setUser(null);
      setLoading(false);
      // If the user was logged out because of invalid email, set error object
      if (invalidEmailError) {
        setError(
          new Error("Please use your Gojek email ID to login to Coffer.")
        );
      } else {
        setError(null);
      }
    });
  };

  /**
   *
   * Called when either login or logout from google fails
   * @param {Object} res - Contains error response object from google
   */
  const onFailure = (res) => {
    console.error("Login / Logout failed. Response Object:", res);
    setLoading(false);
    setError(res);
    deleteCookie("user");
    setUser(null);
  };

  /**
   *
   * Called when the initialization is complete. Initialization also checks if the user
   * is already signed in or not
   * @param {boolean} isSignedIn - If the user is signed in or not
   */
  const onAutoLoadFinish = (isSignedIn) => {
    if (isSignedIn) {
      setLoading(true);
    } else {
      setLoading(false);
    }
  };

  /**
   *
   * Instantiate useGoogleLogin hook. Returns signIn method which signs in the user to google.
   */
  const signIn = useGoogleLogin({
    onSuccess: onLoginSuccess,
    onFailure,
    clientId,
    jsSrc: "https://apis.google.com/js/api:client.js",
    uxMode: isSafari ? "popup" : "redirect", // Using redirect uxmode doesn't work in safari incase the website tracking is off in the browser settings
    isSignedIn: true,
    onAutoLoadFinished: onAutoLoadFinish,
    accessType: "offline",
  });

  /**
   *
   * Signs out the user from google. Also sets `isInvalidEmailError` to true if the user was logged
   * out due to logging in via invalid email.
   * @param {boolean} isInvalidEmailError - Whether the user was logged out due to invalid email or not
   */
  const signOut = (isInvalidEmailError) => {
    deleteCookie("token");
    onLogoutSuccess();
    googleLogout();
    invalidEmailError = isInvalidEmailError;
    window.location.href = `/${routes.LOGIN}`;
  };

  return [user, loading, error, signIn, signOut, onLoginSuccess];
}
